import type { HumanStepRequestedPayload } from "../../../api/threads/liveMessages";
import { isJsonObject } from "../../../api/http/jsonDecoding";
import { decodeVitaAssembly, type VitaAssembly } from "../../../api/threads/batchesApi";
import { RELEASE_PARTS, type ReleasePart } from "../../knowledgeData/releases/releaseRequest";

/**
 * A human step's response definition (workflows.md W-11), read once for the answer form
 * and for the log, which shows an answer beside what it reviewed.
 */

export type HumanStepInput = HumanStepRequestedPayload["inputs"][number];

export type RequestTranslation = NonNullable<HumanStepRequestedPayload["translations"]>[string];

/**
 * What a person step's request shows in the person's language, as the request recorded it
 * from its pinned definition (W-22, T-1b): that language's translation, else its primary
 * language's (`de` for `de-AT`); `null` for the definition's own text.
 */
export function requestTranslation(payload: HumanStepRequestedPayload, language: string | undefined): RequestTranslation | null {
  const translations = payload.translations ?? null;
  if (translations === null || language === undefined) return null;
  return translations[language] ?? translations[language.split("-")[0] ?? language] ?? null;
}

/**
 * The common part of every review (Johannes, 3 Nov: "review steps share a common class right?"; proposal 0.8, O1),
 * as the server's `ReviewResponse` has it: the input it reviews, whether each answer needs a comment, and whether it
 * offers the one talk with the agent that made what it reviews (F105, E2; the server decides). Each kind adds its own decisions.
 */
export interface ReviewPart {
  readonly input: string;
  readonly commentRequired: boolean;
  readonly talk: boolean;
}

const REVIEW_KINDS = ["review_output", "review_change_set", "decide_results", "review_memory"] as const;
type ReviewKind = (typeof REVIEW_KINDS)[number];

export type ResponseDefinition =
  | { readonly kind: "text" }
  | { readonly kind: "choice"; readonly options: readonly { key: string; label: string }[] }
  | ({ readonly kind: "review_output" } & ReviewPart)
  | { readonly kind: "workspace_file"; readonly suffixes: readonly string[] }
  | { readonly kind: "release_part"; readonly part: ReleasePart; readonly input: string | null }
  /** Curators decide the change set the input names, in the review view (ADR-0026 D2, ADR-0031 D4). */
  | ({ readonly kind: "review_change_set" } & ReviewPart)
  /** Curators accept or reject the knowledge-data result the input names (ADR-0026 slice 3a). */
  | ({ readonly kind: "decide_results" } & ReviewPart)
  /** The person edits in the item's form and answers with the change set they saved (ADR-0031 D2). */
  | { readonly kind: "propose_change" }
  /** The person keeps the passages the inputs name as a memory (ADR-0031 D5). */
  | { readonly kind: "keep_memory" }
  /** Someone who may approve confirms or discards the memory the input names (ADR-0031 D5). */
  | ({ readonly kind: "review_memory" } & ReviewPart);

/** A review's common part; `null` for a step that reviews nothing. */
export function reviewPartOf(definition: ResponseDefinition): ReviewPart | null {
  return "talk" in definition ? { input: definition.input, commentRequired: definition.commentRequired, talk: definition.talk } : null;
}

function isReviewKind(kind: unknown): kind is ReviewKind {
  return REVIEW_KINDS.some((review) => review === kind);
}

/** The step's response definition, read leniently: the entry stores it as plain JSON. */
export function responseDefinition(response: HumanStepRequestedPayload["response"]): ResponseDefinition {
  const kind = response["kind"];
  if (kind === "choice" && Array.isArray(response["options"])) {
    const options = (response["options"] as unknown[]).flatMap((option) => {
      if (!isJsonObject(option)) return [];
      const { key, label } = option;
      return typeof key === "string" && typeof label === "string" ? [{ key, label }] : [];
    });
    return { kind: "choice", options };
  }
  if (isReviewKind(kind) && typeof response["input"] === "string") {
    return { kind, input: response["input"], commentRequired: response["comment"] === "required", talk: typeof response["talk"] === "string" };
  }
  if (kind === "release_part" && isReleasePart(response["part"])) {
    return { kind: "release_part", part: response["part"], input: typeof response["input"] === "string" ? response["input"] : null };
  }
  if (kind === "workspace_file") {
    const suffixes = Array.isArray(response["suffixes"]) ? (response["suffixes"] as unknown[]).filter((suffix): suffix is string => typeof suffix === "string") : [];
    return { kind: "workspace_file", suffixes };
  }
  if (kind === "propose_change" || kind === "keep_memory") {
    return { kind };
  }
  return { kind: "text" };
}

/** The change set a review step decides, from the input its request froze; `null` for any other step. */
export function reviewedChangeSetOf(payload: HumanStepRequestedPayload): string | null {
  const definition = responseDefinition(payload.response);
  if (definition.kind !== "review_change_set") return null;
  const value = payload.inputs.find((input) => input.slot === definition.input)?.value;
  return value?.output_kind === "change_set" ? value.change_set_id : null;
}

/**
 * How an acceptance step names what it decides: one result, by the path entry that answered
 * its review or by key, or an assembled vita, whose parts its one answer decides (E4).
 */
export type DecidedResult = { readonly entry: number } | { readonly key: string } | { readonly assembly: VitaAssembly };

/** The schema of an assembled vita's manifest item (`dmw-abox-batch/1`). */
const ASSEMBLED_VITA_SCHEMA = "dmw-abox-batch/1";

/**
 * The result an acceptance step decides (ADR-0026 slice 3a), as its request froze it: the
 * reviewed output of the step before, recorded at the path entry that answered it (in a
 * fork that entry may belong to its parent, so its Thread is read from the path), or the
 * key a run was given. `null` for any other step.
 */
export function decidedResultOf(payload: HumanStepRequestedPayload): DecidedResult | null {
  const definition = responseDefinition(payload.response);
  if (definition.kind !== "decide_results") return null;
  const input = payload.inputs.find((candidate) => candidate.slot === definition.input);
  const value = input?.value;
  const kind = value?.output_kind;
  if ((kind === "annotations" || kind === "instance_data") && input?.from_entry != null) return { entry: input.from_entry };
  if (value?.output_kind === "value" && value.value_kind === "item") {
    // An item is decided only as an assembled vita (the server's `decided_assembly`).
    const artifact = isJsonObject(value.value) ? value.value["content"] : undefined;
    return isJsonObject(artifact) && artifact["schema"] === ASSEMBLED_VITA_SCHEMA ? { assembly: decodeVitaAssembly(artifact["content"]) } : null;
  }
  const key = inputText(payload, definition.input);
  return key === null ? null : { key };
}

/** The input a review step reviews, or a release step builds on, as its request froze it; `undefined` for any other step. */
export function reviewedInputOf(payload: HumanStepRequestedPayload): HumanStepInput | undefined {
  const definition = responseDefinition(payload.response);
  const slot = definition.kind === "review_output" || definition.kind === "release_part" ? definition.input : null;
  return slot === null ? undefined : payload.inputs.find((input) => input.slot === slot);
}

function isReleasePart(value: unknown): value is ReleasePart {
  return RELEASE_PARTS.some((part) => part === value);
}

/**
 * The input of an "Edit an item" step that names the item, an exact `VersionRef` given when
 * the step ran (ADR-0031 D2). Text until ADR-0032's open types give it a type of its own.
 */
export const EDITED_ITEM_INPUT = "item";

/** The item an edit step edits, from the input its request froze; `null` for any other step. */
export function editedItemOf(payload: HumanStepRequestedPayload): string | null {
  return responseDefinition(payload.response).kind === "propose_change" ? inputText(payload, EDITED_ITEM_INPUT) : null;
}

/** The text a frozen input holds: its value if it is text, else what the server rendered. */
export function inputText(payload: HumanStepRequestedPayload, slot: string): string | null {
  const input = payload.inputs.find((candidate) => candidate.slot === slot);
  const value = input?.value;
  const text = value?.output_kind === "value" && typeof value.value === "string" ? value.value : input?.text;
  return text === undefined || text.trim() === "" ? null : text.trim();
}

/** The memory a memory review step decides, from its frozen input; `null` for any other step. */
export function reviewedMemoryOf(payload: HumanStepRequestedPayload): string | null {
  const definition = responseDefinition(payload.response);
  return definition.kind === "review_memory" ? inputText(payload, definition.input) : null;
}

